import { useState } from "react";
import { SignupFormData } from "../model/signup-types";
import { authAPI } from "../../auth/api/auth-api";
import { tokenStorage } from "../../../shared/lib/token-storage";
import { showAlert } from "../../../shared/lib/alert";
import { useMemberStore } from "../../auth/stores/member-store";

/**
 * 전화번호에서 숫자만 추출
 */
function extractPhoneNumber(
  phoneNumber: string | undefined
): string | undefined {
  if (!phoneNumber) return undefined;
  return phoneNumber.replace(/\D/g, "");
}

/**
 * useSignup - 회원가입 API 호출 및 토큰 저장 처리
 */
export function useSignup() {
  const [isLoading, setIsLoading] = useState(false);

  const signup = async (data: SignupFormData) => {
    setIsLoading(true);

    try {
      // API 호출하여 회원가입 완료
      const response = await authAPI.signup({
        provider: data.provider,
        user_type: data.user_type,
        user_id: data.user_id,
        user_phone: extractPhoneNumber(data.user_phone),
        email: data.email,
        gender: data.gender,
        username: data.username,
        password: data.password,
        address: data.address,
        address_detail: data.address_detail,
        zip_code: data.zip_code,
        manager_name: data.manager_name || undefined,
        manager_phone: extractPhoneNumber(data.manager_phone),
        business_number: data.business_number || undefined,
        term_agreements: true,
      });

      // 토큰 저장
      if (response.data?.accessToken) {
        tokenStorage.setAccessToken(response.data.accessToken);
      }
      if (response.data?.refreshToken) {
        tokenStorage.setRefreshToken(response.data.refreshToken);
      }
      if (response.data?.userInfo?.user_id) {
        tokenStorage.setProfileId(response.data.userInfo.user_id);
      }

      // 사용자 정보를 store에 저장 (GNB 업데이트를 위해)
      if (response.data?.userInfo) {
        const { setUser } = useMemberStore();
        // API 응답의 user_id를 User 타입의 id로 매핑
        const userData = {
          id: response.data.userInfo.user_id || response.data.userInfo.id,
          email: response.data.userInfo.email,
          name: response.data.userInfo.name || response.data.userInfo.username,
        };
        setUser(userData);
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "회원가입에 실패했습니다";
      showAlert(errorMessage);
      console.error("Signup error:", error);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { signup, isLoading };
}
